import { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { skip, take, sortBy } from 'lib/array/arrayUtils'
import useToggle from 'hooks/useToggle'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Expander from 'components/utils/Expander'
import ExperienceTicketItem from './ExperienceTicketItem'
import Heading from 'LuxKit/Typography/Heading'
import { FormattedMessage, useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'

const TicketOptions = styled.div`
  > *:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const AdditionalTicketOptions = styled(TicketOptions)`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const CenteredToggle = styled(SmallToggleButton)<{ $topSpacing?: boolean }>`
  display: flex;
  margin-inline: auto;
  margin-block-start: ${props => props.$topSpacing ? rem(16) : 0};
`

const RoundedInfoBanner = styled(MessageBanner)`
  border-radius: ${props => props.theme.borderRadius.S};
`

interface Props {
  onTicketCountChange?: (ticket: App.ExperienceBookingTicketView, nextCount: number) => void
  ticketCounts: any
  ticketOptions: Array<App.ExperienceBookingTicketView>
  purchaseLimitRemaining?: number
  purchaseLimitReason?: App.ExperiencePurchaseLimit['maxReason']
  orderMode?: App.ExperienceOrderMode
  bookingType?: App.ExperienceOffer['bookingType']
  className?: string
  afterHeading?: React.ReactNode
  performanceWarning?: string
  hideAvailabilityBanner?: boolean
  offer: App.ExperienceOffer
  viewMoreToggleTopSpacing?: boolean
}

const initialMaxVisibleOptions = 4

function ExperienceTicketSelection(props: Props) {
  const {
    ticketOptions,
    ticketCounts,
    onTicketCountChange,
    className,
    purchaseLimitRemaining,
    purchaseLimitReason,
    orderMode = 'booking',
    bookingType,
    afterHeading,
    performanceWarning,
    hideAvailabilityBanner,
    offer,
    viewMoreToggleTopSpacing = false,
  } = props
  const [showAll, toggleShowAll] = useToggle()
  const intl = useIntl()

  const sortedTickets = useMemo(() => sortBy(ticketOptions, ticket => !!ticket.isExtra, 'asc'), [ticketOptions])

  return <div className={className}>
    {sortedTickets.length > 0 && <>
      <VerticalSpacer gap={16}>
        <Heading variant="heading6">{intl.formatMessage({
          id: '-nfbSFTi4dJZxPco0s_X',
          defaultMessage: 'Select an option',
          description: 'Section heading for experience ticket option selection',
        })}
        </Heading>
        {afterHeading}
        {performanceWarning && (
          <BodyText variant="small" colour="warning">{performanceWarning}</BodyText>
        )}
        {bookingType === 'NO-CALENDAR-FIXED-END' && !hideAvailabilityBanner && <>
          <RoundedInfoBanner
            kind="informational"
            density="compact"
            icon={<LineInfoCircleIcon />}
          >
            <FormattedMessage
              id="2gjWOv2ze2UTrynIZGfY"
              defaultMessage="This experience is only available on selected days. Please <link>check the description</link> for details."
              description="Info banner shown above ticket options for voucher-based experiences with NO-CALENDAR-FIXED-END booking type, indicating the experience is not available every day. The 'link' placeholder wraps the clickable text that opens the experience offer page in a new tab."
              values={{
                link: (chunks) => <TextLink to={getExperienceOfferPageURL(offer)} target="_blank">{chunks}</TextLink>,
              }}
            />
          </RoundedInfoBanner>
          <Divider kind="primary" />
        </>}
      </VerticalSpacer>
      <TicketOptions>
        {take(sortedTickets, initialMaxVisibleOptions).map(ticket =>
          <ExperienceTicketItem
            key={ticket.id}
            ticket={ticket}
            count={ticketCounts[ticket.id]}
            onTicketCountChange={onTicketCountChange}
            purchaseLimitRemaining={purchaseLimitRemaining}
            purchaseLimitReason={purchaseLimitReason}
            orderMode={orderMode}
            offer={offer}
          />)}
      </TicketOptions>
      {sortedTickets.length > initialMaxVisibleOptions && <>
        <Expander expanded={showAll}>
          <AdditionalTicketOptions>
            {skip(sortedTickets, initialMaxVisibleOptions).map(ticket =>
              <ExperienceTicketItem
                key={ticket.id}
                ticket={ticket}
                count={ticketCounts[ticket.id]}
                onTicketCountChange={onTicketCountChange}
                purchaseLimitRemaining={purchaseLimitRemaining}
                purchaseLimitReason={purchaseLimitReason}
                orderMode={orderMode}
                offer={offer}
              />)}
          </AdditionalTicketOptions>
        </Expander>
        <CenteredToggle
          onClick={toggleShowAll}
          size="medium"
          on={showAll}
          $topSpacing={viewMoreToggleTopSpacing}
        >
          {!showAll && <>{intl.formatMessage(
            {
              id: 'hfwc5RVWzwBw9B1evwDY',
              defaultMessage: 'View {count, plural, one {# more option} other {# more options}}',
              description: 'Toggle button to show additional hidden ticket options',
            },
            { count: sortedTickets.length - initialMaxVisibleOptions },
          )}
          </>}
          {showAll && <>{intl.formatMessage({
            id: '3o4EHssVvlj4rSXDBubi',
            defaultMessage: 'View less options',
            description: 'Toggle button to collapse additional ticket options',
          })}
          </>}
        </CenteredToggle>
      </>}
    </>}
  </div>
}

export default ExperienceTicketSelection
